import { ServiceEndpoints } from "../../constants/ServiceEndpoints";
import {
  CalendarData,
  CalendarFilters,
  CreateNineSetupForm,
  NineSetup,
  NineSetupListFilters,
  NineSetupStatus,
  UpdateNineSetupPayload,
  UpdateNineSetupEnquiryPayload,
} from "../../interfaces/NineSetupObject";
import { deleteRequest, getRequest, postRequest } from "../http/httpRequest";
import {
  ApiEnvelopeError,
  coerceSingle,
  unwrapEnvelope,
} from "../helpers/ApiEnvelopeHelper";
import {
  buildCreateNineSetupPayload,
  buildUpdateNineSetupPayload,
  buildUpdateNineSetupEnquiryPayload,
} from "../converters/uiToBackend/NineSetupConvertor";
import {
  convertDeletedNineSetupId,
  convertNineSetup,
  convertNineSetupList,
  convertCalendarData,
} from "../converters/backendToUi/nineSetupConvertor";

/**
 * CRUD client for the `nine_setup` pipeline. Endpoint shapes are confirmed
 * against the User Service OpenAPI spec (nine-setup-controller):
 * https://ninefinds.com/api/v1/user/v3/api-docs
 *
 * Like the onboarding endpoints, this is verb-in-path RPC rather than plain
 * REST. Two consequences worth knowing before calling it:
 *
 *  - UPDATE is a `POST` to a fixed path with the id in the BODY, not a
 *    `PUT /{id}`. `putRequest` is therefore unused here.
 *  - LIST takes NO query parameters — the server returns everything the caller
 *    may see and filtering is done client-side (see applyFilters).
 *
 * All calls are bearer-authenticated and return the standard
 * `generalProcessingStatus / contents / errorInformation` envelope, so
 * unwrapEnvelope does the parsing and throws ApiEnvelopeError on failure.
 *
 * postRequest/getRequest/deleteRequest all take an explicit bearer flag, so
 * BEARER is passed to each of them.
 */
const BEARER = true;

/**
 * Values for the `role` query param on get-nine-setups, which decides WHICH
 * scope the caller is asking about: their own targets as a captain, or the rows
 * they've claimed as a curator. The endpoint declares it REQUIRED, so every call
 * to that list must pass one.
 *
 * Values confirmed 2026-08-02 from the server's own error text: "role must be
 * CREATOR or CURATOR". Note these are NOT the same vocabulary as `role` on
 * CaptainSummaryDTO (CAPTAIN / CURATOR / BOTH) — same concept, different
 * spelling, and sending CAPTAIN here gets the request rejected outright.
 *
 * Kept as constants because the spec types the param as a bare `string` with no
 * enum. Two different failure modes have already been seen: a rejected request
 * (wrong word), and an unfiltered list (right word, wrong case) — which is how
 * the Claimed tab once showed every curator's rows. Worth making it a real enum
 * server-side so both fail loudly.
 */
const ROLE_PARAM = {
  captain: "captain",
  curator: "curator",
  /** A lead's team scope. Requested from the backend (see the "Onboarding
   *  Earnings — Backend Handover", item 1); may not be live yet. */
  lead: "lead",
} as const;

/** Backend errorCode → user-facing copy. Codes not listed fall back to the
 *  server's errorMessage (already human-readable per the API contract). */
const FRIENDLY_MESSAGES: Record<string, string> = {
  // `uq_nine_setup_mobile` — customer_mobile is UNIQUE table-wide, so this
  // fires when ANY captain has already logged this number, not just the caller.
  // The copy deliberately doesn't promise whose row it is (the server won't say).
  DUPLICATE_CUSTOMER_MOBILE:
    "This mobile number is already in the setup pipeline.",
  NINE_SETUP_NOT_FOUND: "This setup no longer exists. Refresh and try again.",
  INVALID_NINE_SETUP_PAYLOAD: "Some required setup details are missing.",
  INVALID_STATUS_TRANSITION: "That status change isn't allowed from here.",
  INVALID_MOBILE_NUMBER: "Enter a valid 10-digit mobile number.",
  CAPTAIN_NOT_FOUND: "This captain no longer exists. Refresh and try again.",
  NOT_A_CAPTAIN: "Only captains can manage setups.",
  CURATOR_NOT_FOUND: "This curator no longer exists. Refresh and try again.",
  // Row-level ownership: a captain editing someone else's setup, or a curator
  // touching a row they haven't claimed.
  FORBIDDEN: "You don't have access to this setup.",
  UNAUTHENTICATED: "Your session has expired. Please log in again.",
};

/** Converts any error thrown by this service into a display string. */
export function toFriendlyNineSetupError(error: unknown): string {
  if (error instanceof ApiEnvelopeError) {
    return FRIENDLY_MESSAGES[error.errorCode] ?? error.message;
  }
  return error instanceof Error
    ? error.message
    : "Something went wrong. Please try again.";
}

/** `{endpoint}/{id}`. Encoded because the id reaches us from server data, not
 *  from a literal — an unencoded segment is how a stray character turns into a
 *  404 that looks like a missing record. */
function withId(endpoint: string, setupId: string): string {
  return `${endpoint}/${encodeURIComponent(setupId)}`;
}

/** `{endpoint}?{query}` — the one way this service builds a query string.
 *  Values are encoded by URLSearchParams; `undefined` entries are dropped so
 *  optional filters need no hand-rolled `if (x) params.x = x` at call sites. */
function withQuery(
  endpoint: string,
  params: Record<string, string | undefined>
): string {
  const defined = Object.entries(params).filter(
    (entry): entry is [string, string] => entry[1] !== undefined
  );
  const query = new URLSearchParams(defined).toString();
  return query ? `${endpoint}?${query}` : endpoint;
}

/**
 * Client-side stand-in for the server-side filtering this endpoint doesn't
 * offer. Applied after conversion so it matches on the normalised status (the
 * converter lower-cases whatever the backend emits) rather than the raw string.
 *
 * Keeping the signature on getNineSetups means callers — and the slice — don't
 * need to care that the narrowing happens here; if the backend later grows
 * query params, this collapses back into the request without touching them.
 */
function applyFilters(
  setups: NineSetup[],
  filters?: NineSetupListFilters
): NineSetup[] {
  // Destructured up front so the closure doesn't depend on TS narrowing
  // `filters` through the guard — it doesn't, across a two-clause condition.
  const status = filters?.status;
  const curatorId = filters?.curatorId;
  if (!status && !curatorId) return setups;

  return setups.filter(
    (setup) =>
      (!status || setup.status === status) &&
      (!curatorId || setup.curatorId === curatorId)
  );
}

/**
 * CREATE — `POST /api/v1/nine-setup/create-nine-setup`.
 * Returns the created row (with the server's defaults and timestamps applied),
 * which the slice upserts. `captainId` is taken from the bearer token.
 */
async function createNineSetup(form: CreateNineSetupForm): Promise<NineSetup> {
  const payload = buildCreateNineSetupPayload(form);
  const response = await postRequest(
    ServiceEndpoints.NINE_SETUP_CREATE,
    payload,
    BEARER
  );
  // coerceSingle: the envelope's `contents` is always an array, even for one row.
  const contents = coerceSingle(await unwrapEnvelope<unknown>(response));
  return convertNineSetup(contents);
}

/**
 * READ (list) — `GET /api/v1/nine-setup/get-nine-setups`.
 * Scoped server-side to what the caller may see. Takes no parameters, so
 * `filters` narrows the result locally — cheap while the list is small, and it
 * means a status chip switch never costs a round trip.
 */
async function getNineSetups(
  filters?: NineSetupListFilters
): Promise<NineSetup[]> {
  return applyFilters(await getSetupsByRole(ROLE_PARAM.captain), filters);
}

/**
 * The one call behind every `role`-scoped list (captain, curator, lead).
 * `role` is REQUIRED on the endpoint — omitting it is not "unscoped", it's a
 * malformed request — and an unrecognised value may come back as an UNFILTERED
 * list rather than an error, so the value always comes from ROLE_PARAM. The
 * converter is shape-tolerant (wrapper object, array-wrapped wrapper, or raw rows).
 */
async function getSetupsByRole(
  role: (typeof ROLE_PARAM)[keyof typeof ROLE_PARAM]
): Promise<NineSetup[]> {
  const response = await getRequest(
    withQuery(ServiceEndpoints.NINE_SETUP_LIST, { role }),
    BEARER
  );
  const contents = await unwrapEnvelope<unknown>(response);
  return convertNineSetupList(contents);
}

/**
 * BY STATUS — `GET /api/v1/nine-setup/get-nine-setups-by-status?status=…`.
 *
 * The one nine-setup endpoint that takes a query parameter. Used for the
 * curation queue (`approved`), and general enough to serve any later
 * status-scoped list without a new call.
 *
 * Unlike getNineSetups this is not scoped to the caller's own rows — `approved`
 * returns the shared pool every curator picks from, i.e. other captains' work.
 *
 * The status is encoded rather than interpolated: it's a union today, but hand
 * -built query strings are how a future value with a stray character turns into
 * a silently empty list.
 */
async function getNineSetupsByStatus(
  status: NineSetupStatus
): Promise<NineSetup[]> {
  const response = await getRequest(
    withQuery(ServiceEndpoints.NINE_SETUP_BY_STATUS, { status }),
    BEARER
  );
  const contents = await unwrapEnvelope<unknown>(response);
  return convertNineSetupList(contents);
}

/**
 * A CURATOR'S OWN ROWS — `GET /api/v1/nine-setup/get-nine-setups?role=curator`.
 *
 * The same list endpoint as getNineSetups, asking for the other scope: rows
 * where the caller is the CURATOR rather than the captain. Backs the Claimed tab.
 *
 * Note the failure mode if the value is ever wrong: the param is typed as a
 * bare string, so an unrecognised one comes back as an unfiltered list — every
 * curator's rows, silently, with no error. That's how the Claimed tab first went
 * wrong. See ROLE_PARAM.
 */
async function getCuratorSetups(): Promise<NineSetup[]> {
  return getSetupsByRole(ROLE_PARAM.curator);
}

/**
 * A LEAD'S TEAM ROWS — `GET /api/v1/nine-setup/get-nine-setups?role=lead`.
 *
 * Every setup owned by a captain on the caller's team; each carries
 * `leadCommission`, the caller's 15%. Backs the lead's pending earnings.
 *
 * WARNING: an unrecognised `role` may come back as an UNFILTERED list rather
 * than an error (see getSetupsByRole). Callers must therefore keep only rows
 * whose `captainId` is on the caller's own roster — never trust the scope
 * blindly.
 */
async function getTeamSetups(): Promise<NineSetup[]> {
  return getSetupsByRole(ROLE_PARAM.lead);
}

/**
 * READ (single) — `GET /api/v1/nine-setup/get-nine-setup/{id}`.
 * Not used by the list flow, which already holds every row; reach for it when a
 * detail view is deep-linked and state may be cold.
 */
async function getNineSetupById(setupId: string): Promise<NineSetup> {
  const response = await getRequest(
    withId(ServiceEndpoints.NINE_SETUP_GET, setupId),
    BEARER
  );
  const contents = coerceSingle(await unwrapEnvelope<unknown>(response));
  return convertNineSetup(contents);
}

/**
 * UPDATE — `POST /api/v1/nine-setup/update-nine-setup`.
 *
 * A POST, not a PUT, and the id travels in the body (see the payload builder).
 * Partial: only the keys present on `payload` are sent. Lifecycle timestamps are
 * stamped server-side from the status transition, so the returned row — not the
 * local one — is what the slice keeps.
 */
async function updateNineSetup(
  payload: UpdateNineSetupPayload
): Promise<NineSetup> {
  const body = buildUpdateNineSetupPayload(payload);
  const response = await postRequest(
    ServiceEndpoints.NINE_SETUP_UPDATE,
    body,
    BEARER
  );
  const contents = coerceSingle(await unwrapEnvelope<unknown>(response));
  return convertNineSetup(contents);
}

/**
 * DELETE — `DELETE /api/v1/nine-setup/delete-nine-setup/{id}`.
 *
 * Resolves to the deleted id so the slice can drop the row without trusting the
 * response body: a DELETE may return the removed row, just `{ id }`, or nothing
 * at all, and all three are handled. unwrapEnvelope is still awaited — it is
 * what turns a failure envelope into a throw.
 *
 * Note this is a HARD delete. `rejected` and `shelved` are the soft outcomes
 * and belong in `status`; reach for this only when a row was created in error.
 */

/**
 * ADMIN ENQUIRY UPDATE - POST /api/v1/nine-setup/update-nine-setup-enquiry.
 */
async function updateNineSetupEnquiry(
  payload: UpdateNineSetupEnquiryPayload
): Promise<NineSetup> {
  const body = buildUpdateNineSetupEnquiryPayload(payload);
  const response = await postRequest(
    ServiceEndpoints.NINE_SETUP_UPDATE_ENQUIRY,
    body,
    BEARER
  );
  const contents = coerceSingle(await unwrapEnvelope<unknown>(response));
  return convertNineSetup(contents);
}

async function deleteNineSetup(setupId: string): Promise<string> {
  const response = await deleteRequest(
    withId(ServiceEndpoints.NINE_SETUP_DELETE, setupId),
    BEARER
  );
  const contents = await unwrapEnvelope<unknown>(response);
  return convertDeletedNineSetupId(contents, setupId);
}

/**
 * CALENDAR REPORT — `GET /api/v1/nine-setup/onboardings-by-day?from&to&status`.
 *
 * Setups that entered `status` on each day of [from, to], grouped by month and
 * day with per-captain counts. Admin-only; the response shape is
 * NineSetupReportDTO (see convertCalendarData).
 */
async function getCalendarData(filters: CalendarFilters): Promise<CalendarData> {
  const response = await getRequest(
    withQuery(ServiceEndpoints.NINE_SETUP_CALENDAR, {
      from: filters.from,
      to: filters.to,
      status: filters.status,
    }),
    BEARER
  );
  const contents = await unwrapEnvelope<unknown>(response);
  return convertCalendarData(contents);
}

const NineSetupApiService = {
  createNineSetup,
  getNineSetups,
  getTeamSetups,
  getNineSetupById,
  getNineSetupsByStatus,
  getCuratorSetups,
  updateNineSetup,
  updateNineSetupEnquiry,
  deleteNineSetup,
  getCalendarData,
};

export default NineSetupApiService;
